/**
 * Onboarding auto-tune dashboard (DD-NEXT-7) — Zod schemas.
 *
 * Wire shape mirrors the Go `DashboardState` / `StateRow` in
 * internal/tenant/onboardingautotune/reader.go. The backend
 * initialises Cells non-nil so `cells` is always `[]`, never
 * `null`, on an empty tenant — a server regression that returns
 * `null` surfaces here as a parse error rather than a runtime
 * crash, matching the onboarding-health contract.
 */

import { z } from "zod";

export const autotuneCellSchema = z.object({
  // Free-text by design (the 9-class taxonomy is owned by the Go
  // classifier). Bounded length for defense in depth.
  device_class: z.string().min(1).max(64),
  playbook_id: z.string().max(64),
  baseline_method: z.string().max(64),
  // Empty string when the gate held — no shift warranted.
  preferred_method: z.string().max(64),
  baseline_attempts: z.number().int().nonnegative(),
  baseline_successes: z.number().int().nonnegative(),
  preferred_attempts: z.number().int().nonnegative(),
  preferred_successes: z.number().int().nonnegative(),
  // Wilson 95% lower bounds the gate decision was computed from.
  baseline_wilson_lower: z.number().min(0).max(1),
  preferred_wilson_lower: z.number().min(0).max(1),
  // The statistics cleared the gate. Whether it reaches a user
  // still depends on `enabled` (the kill switch) AND the in-code
  // phishing-resistance floor in the recommender.
  actionable: z.boolean(),
  window_start: z.string(),
  window_end: z.string(),
  computed_at: z.string(),
});
export type AutotuneCell = z.infer<typeof autotuneCellSchema>;

export const autotuneDashboardSchema = z.object({
  // Deny-by-default. false → every cell below is SHADOW: computed
  // but not applied to live recommendations.
  enabled: z.boolean(),
  cells: z.array(autotuneCellSchema),
});
export type AutotuneDashboard = z.infer<typeof autotuneDashboardSchema>;
